/**
 * WaitCall — ожидание звонка-сброса (шаг Flow step="waitcall").
 * Показывает номера для набора, опрашивает /check каждые 3 сек; при confirmed → onConfirmed.
 * Пропсы: req (звонок), phone (номер клиента), onConfirmed(), onBack().
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { Card, FallbackButton, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { ErrorText } from "@/components/ErrorText";
import { BackLink } from "@/components/BackLink";
import { ApiError, authApi, friendlyError, type WaitCallRequest } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";

type Props = {
  req: WaitCallRequest;
  phone: string;
  onConfirmed: () => void;
  onBack: () => void;
};

function timeLeft(exp: string): string {
  const ms = Math.max(0, new Date(exp).getTime() - Date.now());
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatPhoneInternational(phone: string): string {
  const d = phone.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("7")) {
    return `+7 ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
  }
  return phone;
}

// Номер для набора всегда в E.164 с «+», иначе Android набирает «7…» и дозвон не проходит.
function dialNumber(phone: string): string {
  return "+" + phone.replace(/\D/g, "");
}

export function WaitCall({ req, phone, onConfirmed, onBack }: Props) {
  const [left, setLeft] = useState(timeLeft(req.expires_at));
  const [status, setStatus] = useState(req.status);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    const t = setInterval(() => setLeft(timeLeft(req.expires_at)), 1000);
    return () => clearInterval(t);
  }, [req.expires_at]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await authApi.check(req.call_id);
        if (!alive) return;
        setStatus(s.status);
        if (s.status === "confirmed") {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          onConfirmed();
        }
      } catch (e) {
        if (!alive) return;
        setErr(friendlyError(e));
        // конфликт привязки номера/аккаунта — постоянный, опрос прекращаем
        if (e instanceof ApiError && e.status === 409) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    };
    tick();
    pollRef.current = window.setInterval(tick, 3000);
    return () => {
      alive = false;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [req.call_id, onConfirmed]);

  const copy = useCallback(async (s: string) => {
    const ok = await copyToClipboard(s);
    if (ok) {
      setCopied(s);
      setTimeout(() => setCopied((c) => (c === s ? null : c)), 1200);
    }
  }, []);

  const expired = status === "expired" || left === "0:00";

  useBackButton(onBack);
  useMainButton({
    text: expired ? "Запросить заново" : "Изменить номер",
    onClick: onBack,
    visible: expired,
    active: true,
  });

  return (
    <Screen footer={expired ? <FallbackButton text="Запросить заново" onClick={onBack} /> : undefined}>
      <Hero
        icon="📞"
        title={expired ? "Время истекло" : "Позвоните на любой из номеров"}
        subtitle={
          expired
            ? "Запросите новый код"
            : `С номера ${formatPhoneInternational(phone)} · сбросится автоматически · осталось ${left}`
        }
      />

      {!expired && (
        <Card className="space-y-2">
          {req.call_phones.map((p) => (
            <div key={p} className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-4 py-3">
              <a href={`tel:${dialNumber(p)}`} className="font-mono text-[18px] text-brand">
                {formatPhoneInternational(p)}
              </a>
              <button onClick={() => copy(dialNumber(p))} className="text-[13px] font-medium text-brand active:opacity-70">
                {copied === dialNumber(p) ? "Скопировано ✓" : "Копировать"}
              </button>
            </div>
          ))}
        </Card>
      )}

      {!expired && (
        <Note className="mt-5">
          Позвоните с того же номера, который вы ввели. Звонок будет сброшен — это бесплатно. После сброса
          подтверждение поступит автоматически.
        </Note>
      )}

      {status === "pending" && !expired && (
        <div className="mt-5 flex items-center justify-center gap-2 text-[14px] text-white/45">
          <Spinner />
          Ждём ваш звонок…
        </div>
      )}

      {err && <ErrorText className="mt-4">{err}</ErrorText>}

      <BackLink onClick={onBack} className="mt-6 w-full py-2 text-center">
        ← Изменить номер
      </BackLink>
    </Screen>
  );
}
